import { useEffect, useMemo, useState } from "react";
import { api, formatEuro } from "@/lib/api";
import MapView from "@/components/MapView";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { TrendUp, Gavel, MapPin, Selection, X, FloppyDisk } from "@phosphor-icons/react";
import TrendChart from "@/components/TrendChart";
import CityCombobox from "@/components/CityCombobox";
import InfoTip from "@/components/InfoTip";
import ServiceIcon from "@/components/ServiceIcon";
import { useTutorial } from "@/context/TutorialContext";

export default function Dashboard() {
  const { maybeAutoStart } = useTutorial();
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState("milano");
  const [grid, setGrid] = useState(null);
  const [listings, setListings] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [showListings, setShowListings] = useState(true);
  const [showAuctions, setShowAuctions] = useState(true);
  const [minYield, setMinYield] = useState(0);
  const [selectedCell, setSelectedCell] = useState(null);
  const [drawMode, setDrawMode] = useState(false);
  const [drawnBBox, setDrawnBBox] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [zoneName, setZoneName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data));
    maybeAutoStart();
  }, [maybeAutoStart]);

  useEffect(() => {
    if (!city) return;
    Promise.all([
      api.get(`/grid?city=${city}&cells=14`),
      api.get(`/listings?city=${city}&limit=250`),
      api.get(`/auctions?city=${city}`),
    ]).then(([g, l, a]) => {
      setGrid(g.data);
      setListings(l.data);
      setAuctions(a.data);
      setSelectedCell(null);
    });
  }, [city]);

  const topCells = useMemo(() => {
    if (!grid?.cells) return [];
    return [...grid.cells]
      .filter((c) => c.yield_pct != null && c.yield_pct >= minYield)
      .sort((a, b) => b.yield_pct - a.yield_pct)
      .slice(0, 10);
  }, [grid, minYield]);

  const cityMeta = cities.find((c) => c.id === city);

  const filteredListings = useMemo(() => {
    if (!drawnBBox) return listings;
    return listings.filter((l) => l.lat >= drawnBBox.lat_min && l.lat <= drawnBBox.lat_max && l.lng >= drawnBBox.lng_min && l.lng <= drawnBBox.lng_max);
  }, [listings, drawnBBox]);
  const filteredAuctions = useMemo(() => {
    if (!drawnBBox) return auctions;
    return auctions.filter((a) => a.lat >= drawnBBox.lat_min && a.lat <= drawnBBox.lat_max && a.lng >= drawnBBox.lng_min && a.lng <= drawnBBox.lng_max);
  }, [auctions, drawnBBox]);

  const saveDrawnZone = async () => {
    if (!zoneName.trim() || !drawnBBox) return;
    setSaving(true);
    try {
      await api.post("/saved-zones", {
        name: zoneName.trim(), city, kind: "bbox", bbox: drawnBBox,
        min_yield: minYield, min_discount: 20, email_alerts: true,
      });
      toast.success("Area salvata. Riceverai alert per nuove aste.");
      setSaveOpen(false);
      setZoneName("");
    } catch (e) {
      toast.error("Errore nel salvataggio della zona");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-56px)] bg-zinc-950 text-zinc-50">
      {/* Sidebar */}
      <aside className="w-[380px] shrink-0 border-r border-white/10 flex flex-col bg-zinc-950" data-testid="dashboard-sidebar">
        <div className="p-5 border-b border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <ServiceIcon name="market" size={36} />
            <div>
              <div className="font-mono-data text-[10px] text-lime-400 tracking-widest">— MARKET ANALYSIS</div>
              <div className="font-display font-black text-sm text-zinc-100 mt-0.5">Configura l'area</div>
            </div>
          </div>
          <div className="space-y-4">
            <div data-tour-id="tour-city">
              <div className="flex items-center gap-1.5">
                <Label className="font-mono-data text-xs text-zinc-400 tracking-wider">CITTÀ</Label>
                <InfoTip title="Ricerca città" testId="tip-city" side="right">
                  Digita il nome. Se la città non è in lista, ImmoScope la aggiunge in tempo reale da OpenStreetMap. Funziona per qualsiasi comune italiano.
                </InfoTip>
              </div>
              <div className="mt-1.5">
                <CityCombobox value={city} onChange={setCity} cities={cities} testId="select-city" />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label className="font-mono-data text-xs text-zinc-400 tracking-wider">MOSTRA INSERZIONI</Label>
              <Switch checked={showListings} onCheckedChange={setShowListings} data-testid="toggle-listings" />
            </div>
            <div className="flex items-center justify-between">
              <Label className="font-mono-data text-xs text-amber-400 tracking-wider">MOSTRA ASTE</Label>
              <Switch checked={showAuctions} onCheckedChange={setShowAuctions} data-testid="toggle-auctions" />
            </div>

            <div data-tour-id="tour-yield">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Label className="font-mono-data text-xs text-zinc-400 tracking-wider">RENDIMENTO MIN.</Label>
                  <InfoTip title="Rendimento minimo" testId="tip-yield" side="right">
                    Filtra le zone per rendimento lordo minimo (affitto annuo / prezzo × 100). Sotto il 4% = zona premium a bassa resa. Sopra il 7% = alta redditività ma controlla il rischio (periferie, sfitto).
                  </InfoTip>
                </div>
                <span className="font-mono-data text-xs text-lime-400 font-bold">{minYield}%</span>
              </div>
              <Slider
                value={[minYield]}
                onValueChange={(v) => setMinYield(v[0])}
                min={0} max={10} step={0.5}
                data-testid="slider-yield"
              />
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 border-b border-white/10">
          <div className="p-4 border-r border-white/10">
            <div className="font-mono-data text-[10px] text-zinc-500 tracking-widest">INSERZIONI</div>
            <div className="font-display font-black text-2xl text-zinc-50 mt-1 font-mono-data">{listings.length}</div>
          </div>
          <div className="p-4">
            <div className="font-mono-data text-[10px] text-amber-400 tracking-widest">ASTE</div>
            <div className="font-display font-black text-2xl text-amber-400 mt-1 font-mono-data">{auctions.length}</div>
          </div>
        </div>

        {/* Top cells */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-2 mb-3">
            <TrendUp size={14} weight="bold" className="text-lime-400" />
            <span className="font-mono-data text-xs text-lime-400 tracking-widest">TOP CELLE PER RENDIMENTO</span>
          </div>
        </div>
        <ScrollArea className="flex-1" data-tour-id="tour-top-cells">
          <div className="divide-y divide-white/5">
            {topCells.map((c) => (
              <button
                key={`${c.i}-${c.j}`}
                onClick={() => setSelectedCell(c)}
                className="w-full text-left p-4 hover:bg-white/5 transition-colors block"
                data-testid={`top-cell-${c.i}-${c.j}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="font-mono-data text-xs text-zinc-400">
                    CELLA #{c.i}-{c.j}
                  </div>
                  <Badge className="rounded-none bg-lime-400 text-lime-950 font-mono-data text-[10px] font-bold hover:bg-lime-400">
                    {c.yield_pct}%
                  </Badge>
                </div>
                <div className="font-mono-data text-xs text-zinc-500 flex justify-between">
                  <span>{c.avg_price_sqm ? `${formatEuro(c.avg_price_sqm)}/m²` : "—"}</span>
                  <span>{c.n_listings} imm.</span>
                </div>
              </button>
            ))}
            {topCells.length === 0 && (
              <div className="p-6 text-center text-xs text-zinc-500 font-mono-data">
                Nessuna cella corrisponde ai filtri
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Trend chart */}
        <div className="border-t border-white/10 p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendUp size={14} weight="bold" className="text-cyan-400" />
            <span className="font-mono-data text-xs text-cyan-400 tracking-widest">TREND ULTIMI 24M</span>
          </div>
          <TrendChart city={city} months={24} height={160} />
        </div>

        {/* Legend */}
        <div className="border-t border-white/10 p-4 space-y-2">
          <div className="font-mono-data text-[10px] text-zinc-500 tracking-widest">LEGENDA</div>
          <div className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 bg-cyan-500" />
            <span className="text-zinc-400">Rendimento basso</span>
            <div className="w-3 h-3 bg-lime-400 ml-auto" />
            <span className="text-zinc-400">Alto</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <div className="w-2 h-2 rounded-full bg-white" />
            <span className="text-zinc-400">Vendita</span>
            <div className="w-2 h-2 rounded-full bg-cyan-500 ml-3" />
            <span className="text-zinc-400">Affitto</span>
            <div className="w-2.5 h-2.5 rounded-full bg-amber-400 ml-3" />
            <span className="text-zinc-400">Asta</span>
          </div>
        </div>
      </aside>

      {/* Map */}
      <main className="flex-1 relative" data-tour-id="tour-map">
        {grid ? (
          <MapView
            grid={grid}
            listings={showListings ? filteredListings : []}
            auctions={showAuctions ? filteredAuctions : []}
            showListings={showListings}
            showAuctions={showAuctions}
            onCellClick={setSelectedCell}
            drawMode={drawMode}
            onBBoxDraw={(b) => { setDrawnBBox(b); setDrawMode(false); toast.success("Area disegnata"); }}
            drawnBBox={drawnBBox}
          />
        ) : (
          <div className="flex items-center justify-center h-full font-mono-data text-xs text-lime-400 animate-pulse">
            CARICAMENTO GRIGLIA...
          </div>
        )}

        {/* Draw controls */}
        <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-2" data-tour-id="tour-draw">
          <Button
            size="sm"
            onClick={() => setDrawMode(!drawMode)}
            className={`rounded-none font-mono-data text-xs h-9 ${drawMode ? "bg-lime-400 text-lime-950 hover:bg-lime-500" : "bg-zinc-900 text-zinc-100 hover:bg-zinc-800 border border-white/10"}`}
            data-testid="btn-draw-mode"
          >
            <Selection size={14} weight="bold" className="mr-1.5" />
            {drawMode ? "CLICCA 2 PUNTI SULLA MAPPA" : "DISEGNA AREA"}
          </Button>
          {drawnBBox && (
            <>
              <Button size="sm" onClick={() => setSaveOpen(true)} className="rounded-none bg-amber-400 text-amber-950 hover:bg-amber-500 font-mono-data text-xs h-9 font-bold" data-testid="btn-save-drawn-zone">
                <FloppyDisk size={14} weight="bold" className="mr-1.5" />SALVA AREA
              </Button>
              <Button size="sm" variant="outline" onClick={() => setDrawnBBox(null)} className="rounded-none border-white/20 text-zinc-300 hover:bg-white/5 font-mono-data text-xs h-9" data-testid="btn-clear-bbox">
                <X size={14} weight="bold" className="mr-1.5" />RIMUOVI AREA
              </Button>
            </>
          )}
        </div>

        {/* Floating status pill */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] pointer-events-none">
          <div className="bg-zinc-950/90 border border-white/10 px-4 py-2 flex items-center gap-3">
            <MapPin size={14} weight="fill" className="text-lime-400" />
            <span className="font-mono-data text-xs text-zinc-100">{cityMeta?.name?.toUpperCase() || "—"}</span>
            <span className="font-mono-data text-[10px] text-zinc-500">·</span>
            <span className="font-mono-data text-[10px] text-zinc-400">{grid?.cells?.length || 0} CELLE ATTIVE</span>
          </div>
        </div>

        {selectedCell && (
          <div className="absolute bottom-4 right-4 z-[1000] w-80 border border-white/10 bg-zinc-950/95 p-5" data-testid="selected-cell-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono-data text-xs text-lime-400 tracking-widest">CELLA SELEZIONATA</div>
              <button onClick={() => setSelectedCell(null)} className="text-zinc-500 hover:text-zinc-100 text-lg leading-none">×</button>
            </div>
            <div className="font-display font-black text-2xl mb-4 font-mono-data">
              #{selectedCell.i}-{selectedCell.j}
            </div>
            <div className="space-y-2 font-mono-data text-sm">
              <div className="flex justify-between border-b border-white/5 pb-1">
                <span className="text-zinc-400">€/m² vendita</span>
                <span className="text-zinc-100">{selectedCell.avg_price_sqm ? formatEuro(selectedCell.avg_price_sqm) : "—"}</span>
              </div>
              <div className="flex justify-between border-b border-white/5 pb-1">
                <span className="text-zinc-400">€/m² affitto</span>
                <span className="text-zinc-100">{selectedCell.avg_rent_sqm ?? "—"}</span>
              </div>
              <div className="flex justify-between border-b border-white/5 pb-1">
                <span className="text-zinc-400">Rendimento</span>
                <span className="text-lime-400 font-bold">{selectedCell.yield_pct ? `${selectedCell.yield_pct}%` : "—"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">Immobili</span>
                <span className="text-zinc-100">{selectedCell.n_listings}</span>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-white/10">
              <div className="flex items-center gap-1.5 text-[11px] text-amber-400 font-mono-data">
                <Gavel size={12} weight="bold" />
                {auctions.filter((a) => {
                  const [[la1, lo1], [la2, lo2]] = selectedCell.bounds;
                  return a.lat >= la1 && a.lat <= la2 && a.lng >= lo1 && a.lng <= lo2;
                }).length} aste in questa cella
              </div>
            </div>
          </div>
        )}
      </main>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="rounded-none bg-zinc-950 border-white/10 text-zinc-50">
          <DialogHeader>
            <DialogTitle className="font-display">Salva area disegnata</DialogTitle>
            <DialogDescription className="text-zinc-400">Dai un nome alla zona per iniziare a monitorarla.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="font-mono-data text-xs text-zinc-400">NOME AREA</Label>
            <Input value={zoneName} onChange={(e) => setZoneName(e.target.value)} placeholder="es. Navigli sud" className="rounded-none bg-zinc-900 border-white/10" data-testid="drawn-zone-name" autoFocus />
            <p className="text-xs text-zinc-500 leading-relaxed">
              Riceverai un alert email quando compaiono nuove aste in questa area (rendimento min ≥ {minYield}%, sconto ≥ 20%).
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)} className="rounded-none border-white/20">Annulla</Button>
            <Button onClick={saveDrawnZone} disabled={saving || !zoneName.trim()} className="rounded-none bg-lime-400 text-lime-950 hover:bg-lime-500 font-bold" data-testid="btn-confirm-save-zone">
              {saving ? "Salvataggio..." : "Salva & monitora"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
